import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { IMAGES, WEDDING_DATE_ISO, HASHTAG } from "@/lib/wedding-data";

const useCountdown = (targetIso) => {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    minutes: Math.floor(diff / 60000) % 60,
    seconds: Math.floor(diff / 1000) % 60,
  };
};

const pad = (n) => String(n).padStart(2, "0");

export default function Hero() {
  const { days, hours, minutes, seconds } = useCountdown(WEDDING_DATE_ISO);
  const units = [
    { label: "Days", value: days, id: "days" },
    { label: "Hours", value: hours, id: "hours" },
    { label: "Minutes", value: minutes, id: "minutes" },
    { label: "Seconds", value: seconds, id: "seconds" },
  ];

  return (
    <section id="top" data-testid="hero-section" className="relative">
      <div className="relative h-[92vh] min-h-[620px] overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="Vishi and Piyush"
          data-testid="hero-image"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-36">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs uppercase tracking-[0.35em] text-[#E1C699] mb-6"
          >
            A formal invitation from
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="font-serif text-5xl sm:text-6xl lg:text-8xl tracking-tighter text-[#FDFBF7] leading-[0.95]"
          >
            Vishi <span className="italic text-[#E1C699]">&</span> Piyush
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-6 text-base md:text-lg text-[#FDFBF7]/85 max-w-xl"
          >
            Sunday, 24 January 2027 · Lelux Resort, Gurgaon
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#rsvp"
              data-testid="hero-rsvp-button"
              onClick={(e) => {
                e.preventDefault();
                window.__lenis?.scrollTo("#rsvp", { offset: -72 });
              }}
              className="rounded-full bg-[#D4AF37] text-[#5D171E] text-xs uppercase tracking-[0.2em] font-medium px-8 py-4 hover:bg-[#E1C699] transition-colors duration-300"
            >
              RSVP Now
            </a>
            <span className="font-serif italic text-2xl text-[#E1C699]">{HASHTAG}</span>
          </motion.div>
        </div>
      </div>

      <div className="relative z-20 max-w-5xl mx-auto px-6 lg:px-12 -mt-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          data-testid="countdown-bar"
          className="gold-frame rounded-2xl bg-[#FDFBF7]/85 backdrop-blur-xl border border-[#D4AF37]/40 px-8 py-8 md:px-14 flex flex-col md:flex-row md:items-center gap-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#5D171E] md:max-w-[140px]">
            Counting down to forever
          </p>
          <div className="flex-1 grid grid-cols-4 gap-4 md:gap-8">
            {units.map((u) => (
              <div key={u.id} className="text-center">
                <div
                  data-testid={`countdown-${u.id}`}
                  className="font-serif text-4xl md:text-5xl text-[#5D171E] tabular-nums"
                >
                  {pad(u.value)}
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#1A1A1A]/60">
                  {u.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
